// 营销落地页 —— Allinks 持卡人获客壳 · 深色 Hero + 电蓝 CTA
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CreditCard, Zap, Shield, Globe, Bitcoin, ArrowRight, Sparkles } from 'lucide-react';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { BrandLogo } from '@/components/BrandLogo';
import { useLocalePath } from '@/i18n/useLocalePath';

export default function LandingPage() {
  const { t } = useTranslation(undefined, { keyPrefix: 'landing' });
  const { localePath: lp } = useLocalePath();

  const features = [
    { icon: <Zap className="w-6 h-6" />, key: 'instant' },
    { icon: <Globe className="w-6 h-6" />, key: 'global' },
    { icon: <Shield className="w-6 h-6" />, key: 'secure' },
    { icon: <Bitcoin className="w-6 h-6" />, key: 'cryptoToFiat' },
  ];

  const steps = [
    { num: '1', key: 'step1' },
    { num: '2', key: 'step2' },
    { num: '3', key: 'step3' },
  ];

  const faqs = [
    { qKey: 'q1', aKey: 'a1' },
    { qKey: 'q2', aKey: 'a2' },
    { qKey: 'q3', aKey: 'a3' },
    { qKey: 'q4', aKey: 'a4' },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <nav className="fixed top-0 inset-x-0 z-50 bg-slate-950/70 backdrop-blur-xl border-b border-white/10 safe-top">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <BrandLogo variant="dark" size="sm" />
          <div className="flex items-center gap-3 sm:gap-4">
            <LanguageSwitcher variant="full" />
            <Link
              to={lp('/login')}
              className="text-sm text-slate-300 hover:text-white transition-colors hidden sm:inline"
            >
              {t('nav.signIn')}
            </Link>
            <Link to={lp('/login')} className="btn-primary text-sm !min-h-[40px] py-2 px-4">
              {t('nav.openWebApp')}
            </Link>
          </div>
        </div>
      </nav>

      <section className="relative pt-28 sm:pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-hero-mesh pointer-events-none" />
        <div className="absolute top-20 right-1/4 w-72 h-72 bg-brand-600/20 rounded-full blur-3xl pointer-events-none" />
        <div className="relative max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/10 rounded-full px-4 py-1.5 mb-6 text-sm backdrop-blur">
            <Sparkles className="w-4 h-4 text-brand-300" />
            <span className="text-slate-200">{t('hero.tagline')}</span>
          </div>
          <h1
            className="text-4xl sm:text-5xl md:text-6xl font-extrabold mb-6 tracking-tight bg-gradient-to-r from-white via-white to-brand-300 bg-clip-text text-transparent"
            dangerouslySetInnerHTML={{ __html: t('hero.title') }}
          />
          <p className="text-base sm:text-lg text-slate-400 mb-8 max-w-2xl mx-auto leading-relaxed">
            {t('hero.description')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to={lp('/login')} className="btn-primary px-8 py-3.5 text-base w-full sm:w-auto">
              {t('hero.applyCard')} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to={lp('/cardholder/dashboard')}
              className="btn-secondary px-8 py-3.5 text-base w-full sm:w-auto !bg-white/10 !text-white !border-white/20 hover:!bg-white/20"
            >
              {t('hero.openApp')}
            </Link>
          </div>

          {/* 设备预览卡 */}
          <div className="mt-16 mx-auto max-w-sm">
            <div className="rounded-[2rem] border border-white/10 bg-gradient-to-b from-slate-900 to-slate-950 p-3 shadow-elevated">
              <div className="rounded-[1.5rem] bg-canvas text-slate-900 p-4 text-left">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">
                  Allinks Wallet
                </p>
                <p className="text-3xl font-extrabold text-slate-900 tabular-nums">$1,050.50</p>
                <div className="mt-4 pay-card bg-card-visa h-36 flex flex-col justify-between">
                  <div className="relative z-10 flex justify-between">
                    <span className="text-[10px] text-white/70">Classic Virtual</span>
                    <CreditCard className="w-5 h-5 text-white/80" />
                  </div>
                  <p className="relative z-10 font-mono tracking-widest text-sm">4111 **** **** 1234</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-6 bg-slate-900/50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 tracking-tight">{t('features.title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {features.map((f) => (
              <div
                key={f.key}
                className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-6 hover:border-brand-500/40 hover:bg-white/[0.07] transition-all duration-300"
              >
                <div className="w-12 h-12 bg-brand-600/20 rounded-xl flex items-center justify-center text-brand-300 mb-4">
                  {f.icon}
                </div>
                <h3 className="font-semibold mb-2">{t(`features.${f.key}.title`)}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">
                  {t(`features.${f.key}.desc`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 tracking-tight">{t('howItWorks.title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((s) => (
              <div key={s.num} className="text-center">
                <div className="w-14 h-14 bg-brand-gradient rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4 shadow-elevated">
                  {s.num}
                </div>
                <h3 className="font-semibold mb-2">{t(`howItWorks.${s.key}.title`)}</h3>
                <p className="text-sm text-slate-400">{t(`howItWorks.${s.key}.desc`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-6 bg-slate-900/50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-10 tracking-tight">{t('faq.title')}</h2>
          <div className="space-y-3">
            {faqs.map((f) => (
              <details
                key={f.qKey}
                className="group rounded-2xl border border-white/10 bg-white/5 px-5 py-4 open:bg-white/[0.07]"
              >
                <summary className="font-semibold cursor-pointer list-none flex justify-between items-center">
                  {t(`faq.${f.qKey}`)}
                  <span className="text-slate-500 group-open:rotate-45 transition-transform text-xl leading-none">
                    +
                  </span>
                </summary>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed">{t(`faq.${f.aKey}`)}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 py-10 px-6 text-center text-sm text-slate-500">
        <BrandLogo variant="dark" size="sm" className="justify-center mb-3" />
        <p>{t('footer')}</p>
      </footer>
    </div>
  );
}
